"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Comportamento de trilho horizontal — uma implementação só para TODO carrossel
 * de conteúdo do app (vitrines de produto, barra de categorias, listagens).
 *
 * Três decisões que definem a fluidez, e o porquê de cada uma:
 *
 * 1. TOUCH É NATIVO. Não existe drag custom reescrevendo `scrollLeft` no
 *    `touchmove`. O gesto do dedo já rola o container; um handler por cima
 *    SOMA ao movimento nativo (o antigo multiplicava por 3, então o trilho
 *    andava ~4× o dedo) e mata o fling — ao soltar, o scroll parava seco em
 *    vez de desacelerar. Sem handler, o navegador entrega arrasto livre,
 *    inércia e rubber-band de graça, na thread de composição. Mesma escolha do
 *    `HorizontalScroller` do big2be-ui (product collection).
 *
 * 2. `scroll-behavior: smooth` NÃO fica no CSS do track. Se ficar, TODA
 *    mudança de `scrollLeft` vira animação de ~500ms — inclusive as do gesto
 *    de roda, que passam a chegar em fila e "escorregar" atrás do cursor. A
 *    suavidade fica só onde é intencional: o `scrollBy` das setas, que pede
 *    `behavior: "smooth"` explicitamente.
 *
 * 3. `overscroll-behavior-x: contain` no track: ao chegar no fim do trilho o
 *    gesto para ali, sem escorrer para o scroll da página nem disparar o
 *    "voltar" por swipe do navegador.
 *
 * As setas medem por ResizeObserver no track E no conteúdo — imagem que carrega
 * depois muda a largura do conteúdo sem mudar a do track, e só observar o track
 * deixava a seta da direita sumida num trilho que rola.
 */

/** Classe do elemento que rola. Ver decisões (2) e (3) no doc acima. */
export const SCROLL_TRACK_CLASS = "w-full overflow-x-auto overscroll-x-contain scrollbar-none";

interface UseHorizontalScrollOptions {
    /** Passo das setas: px ou `"page"` (largura visível + gap). Default 300. */
    step?: number | "page";
    /** Liga a medição de `canLeft`/`canRight` (só quem mostra setas precisa). */
    measure?: boolean;
    /** Liga o atalho Shift + roda → rolagem horizontal. Default true. */
    wheel?: boolean;
}

export function useHorizontalScroll<T extends HTMLElement = HTMLDivElement>({
    step = 300,
    measure = true,
    wheel = true,
}: UseHorizontalScrollOptions = {}) {
    const ref = useRef<T>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const update = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        setCanLeft(el.scrollLeft > 1);
        setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    }, []);

    useEffect(() => {
        if (!measure) return;
        const el = ref.current;
        if (!el) return;

        update();

        const ro = new ResizeObserver(update);
        ro.observe(el);
        // O conteúdo (wrapper interno) muda de largura sozinho quando imagens
        // e fontes chegam — sem observá-lo, a medição inicial fica velha.
        if (el.firstElementChild) ro.observe(el.firstElementChild);

        return () => ro.disconnect();
    }, [measure, update]);

    useEffect(() => {
        if (!wheel) return;
        const el = ref.current;
        if (!el) return;

        // `passive: false` porque o handler chama preventDefault para trocar o
        // eixo do gesto; sem isso o Chrome ignora o preventDefault e a página
        // rola junto.
        const onWheel = (e: WheelEvent) => {
            if (!e.shiftKey) return;
            if (el.scrollWidth <= el.clientWidth) return;
            e.preventDefault();
            el.scrollLeft += Math.abs(e.deltaX) > 0 ? e.deltaX : e.deltaY;
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [wheel]);

    /** Rola um passo — ÚNICO lugar com animação (ver decisão 2). */
    const scrollBy = useCallback(
        (dir: "left" | "right") => {
            const el = ref.current;
            if (!el) return;
            const content = el.firstElementChild as HTMLElement | null;
            const gap = content ? Number.parseFloat(getComputedStyle(content).columnGap) || 0 : 0;
            const amount = step === "page" ? el.clientWidth + gap : step;
            el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
        },
        [step]
    );

    return { ref, canLeft, canRight, update, scrollBy };
}
